/*
 * ThemeProvider — fetches public tenant-branding on mount and sets CSS variables
 * on the document root so every component can read:
 *    var(--tenant-primary), var(--tenant-primary-foreground)
 *    var(--tenant-accent),  var(--tenant-accent-foreground)
 *    var(--tenant-primary-rgb), var(--tenant-accent-rgb)  (for alpha tints)
 *
 * Listens for the custom "tenant-branding-updated" window event so the
 * White-Label Editor can trigger a live refresh without a full reload.
 */
import { useEffect, useState, createContext, useContext, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandingCtx = createContext({
  company_name: "OC Tasker",
  logo_url: "",
  primary_color: "#f97316",
  accent_color: "#059669",
  tagline: "",
});

export const useBranding = () => useContext(BrandingCtx);

// Convert #RRGGBB → "r, g, b" so Tailwind rgba() / CSS color-mix works.
function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return "249, 115, 22";
  return `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`;
}

// Choose readable foreground (black or white) based on luminance of the bg color.
function pickForeground(hex) {
  const rgb = hexToRgb(hex).split(", ").map(Number);
  const [r, g, b] = rgb;
  const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return l > 0.55 ? "#111827" : "#ffffff";
}

function applyBrandingToRoot(b) {
  const root = document.documentElement;
  const primary = b.primary_color || "#f97316";
  const accent = b.accent_color || "#059669";
  root.style.setProperty("--tenant-primary", primary);
  root.style.setProperty("--tenant-primary-rgb", hexToRgb(primary));
  root.style.setProperty("--tenant-primary-foreground", pickForeground(primary));
  root.style.setProperty("--tenant-accent", accent);
  root.style.setProperty("--tenant-accent-rgb", hexToRgb(accent));
  root.style.setProperty("--tenant-accent-foreground", pickForeground(accent));
}

export function ThemeProvider({ children }) {
  const [branding, setBranding] = useState({
    company_name: "OC Tasker",
    logo_url: "",
    primary_color: "#f97316",
    accent_color: "#059669",
    tagline: "",
  });

  const loadBranding = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user-management/tenant-branding`, { timeout: 5000 });
      const b = res.data || {};
      setBranding((prev) => ({ ...prev, ...b }));
      applyBrandingToRoot(b);
      // Set <title> suffix if not already set per-page
      if (b.company_name && !document.title.includes(b.company_name)) {
        // Only override the default title, not per-page SEOHead titles
        if (document.title === "React App" || document.title === "") {
          document.title = b.company_name;
        }
      }
    } catch (e) {
      // Non-fatal — fallback orange/emerald already applied by default CSS.
      // eslint-disable-next-line no-console
      console.warn("Branding fetch failed, using defaults", e?.message);
      applyBrandingToRoot({ primary_color: "#f97316", accent_color: "#059669" });
    }
  }, []);

  useEffect(() => {
    applyBrandingToRoot(branding); // instant defaults before fetch lands
    loadBranding();
    const onUpdate = () => loadBranding();
    window.addEventListener("tenant-branding-updated", onUpdate);
    return () => window.removeEventListener("tenant-branding-updated", onUpdate);
    // eslint-disable-next-line
  }, []);

  return <BrandingCtx.Provider value={branding}>{children}</BrandingCtx.Provider>;
}
